import React, { useMemo } from "react";

import { Grid } from "@material-ui/core";
import { useFormikContext } from "formik";
import { useSelector } from "react-redux";

import { Input, Label } from "components/Form/FormikControls";
import { InlineButton } from "components/Form/FormikControls/Label";
import { InformationPanel } from "components/Layout";
import { Info } from "components/LotCard/Elements";

import { getBuyerInformationString } from "lib/businesses";

import {
  getBidders,
  getBuyerWaysByBusinessId,
  selectBidderByBuyerInformation,
} from "selectors";

type BuyerWayFormValue = { id?: string; name?: string } | null;

interface QuickSellBidderFormValues {
  bidderNumber?: string | number | null;
  buyerId?: string | null;
  destinationPropertyId?: string | null;
  buyerWay?: BuyerWayFormValue;
}

interface QuickSellBidderNumberInputProps {
  disabled?: boolean;
  autoFocus?: boolean;
  inlineButton?: InlineButton | null;
  enableTemporaryBusinessCreation?: boolean;
  /** When true, resolve bidder number from buyer / way / PIC when they change. */
  setBidderNumberFromBuyerDetails?: boolean;
}

export const QuickSellBidderNumberInput = ({
  disabled = false,
  autoFocus = false,
  inlineButton = null,
  enableTemporaryBusinessCreation = false,
  setBidderNumberFromBuyerDetails = false,
}: QuickSellBidderNumberInputProps) => {
  const formikProps = useFormikContext<QuickSellBidderFormValues>();

  const bidderLookup = useSelector(getBidders);

  const { values, setFieldValue, touched } = formikProps;

  const { bidderNumber, buyerId, destinationPropertyId, buyerWay } = values;

  const bidder = useMemo((): BidderRegistration | undefined => {
    const list = Object.values(bidderLookup) as BidderRegistration[];
    return list.find(bidderEntry => {
      return (
        String(bidderEntry.registrationNumber) === String(bidderNumber) &&
        !!bidderEntry.businessId
      );
    });
  }, [bidderLookup, bidderNumber]);

  const buyerInformationString = getBuyerInformationString(
    buyerId,
    destinationPropertyId,
    buyerWay?.name,
  );
  const bidderByBuyerInformationLookup = useSelector(
    selectBidderByBuyerInformation,
  );
  const existingBidder = bidderByBuyerInformationLookup[buyerInformationString];

  const showUpdateBidderNote =
    existingBidder &&
    String(existingBidder.registrationNumber) !== String(bidderNumber);

  const showCreateBidderNote =
    !existingBidder && !!bidderNumber && touched.bidderNumber;

  const buyerWays =
    useSelector(getBuyerWaysByBusinessId(bidder?.businessId)) || [];

  const existingBuyerWay = buyerWays.find(bw => bw.name === bidder?.buyerWay);

  // Bidder number → buyer / way / PIC (only when the number resolves to a real bidder).
  React.useEffect(() => {
    if (bidderNumber == null || bidderNumber === "" || !bidder?.businessId) {
      return;
    }

    setFieldValue("buyerId", bidder.businessId);

    if (existingBuyerWay) {
      setFieldValue("buyerWay", {
        id: existingBuyerWay.id,
        name: existingBuyerWay.name,
      });
    } else if (bidder.buyerWay) {
      setFieldValue("buyerWay", {
        name: bidder.buyerWay,
      });
    } else {
      setFieldValue("buyerWay", null);
    }

    if (bidder.defaultPropertyId) {
      setFieldValue("destinationPropertyId", bidder.defaultPropertyId);
    } else {
      setFieldValue("destinationPropertyId", null);
    }
  }, [bidder, existingBuyerWay, bidderNumber, setFieldValue]);

  // Buyer / way / PIC → bidder number (temp-business flow): fill from lookup, or clear when there is no match.
  React.useEffect(() => {
    if (!setBidderNumberFromBuyerDetails) {
      return;
    }

    if (
      existingBidder &&
      String(existingBidder.registrationNumber) !== String(bidderNumber)
    ) {
      setFieldValue("bidderNumber", existingBidder.registrationNumber);
      return;
    }

    if ((buyerId || buyerWay || destinationPropertyId) && !existingBidder) {
      setFieldValue("bidderNumber", null);
    }
  }, [
    existingBidder,
    buyerId,
    buyerWay,
    destinationPropertyId,
    setBidderNumberFromBuyerDetails,
    setFieldValue,
    bidderNumber,
  ]);

  return (
    <>
      <Label inlineButton={inlineButton}>Bidder Number</Label>
      <Input
        name="bidderNumber"
        disabled={disabled}
        autoFocus={autoFocus}
        type="number"
      />
      {!enableTemporaryBusinessCreation && showUpdateBidderNote && (
        <Info className="p-1">
          <em>
            Note: Setting a new bidder number will update the current bidder
            registration number for this Buyer/Way/PIC combination
          </em>
        </Info>
      )}
      {enableTemporaryBusinessCreation && showCreateBidderNote && (
        <Grid item xs={12}>
          <InformationPanel className="mt-8">
            You&apos;ve entered a new Bidder Number. <br /> A temporary Business
            will be created with a bidder registration on Save. You can update
            this Business later.
          </InformationPanel>
        </Grid>
      )}
    </>
  );
};
